/**
 * TypeScript Interfaces for Instagram Reel Scene Splitting Feature
 * Feature: Scene Splitting for Instagram Reels
 */

import { Id } from '../../convex/_generated/dataModel';
import { ModelKey } from './reels';

// ============================================================================
// Core Type Definitions
// ============================================================================

/**
 * Scene Type Union
 * Defines the visual composition of each scene
 */
export type SceneType =
  | 'avatar_only'
  | 'video_only'
  | 'avatar_broll_split'
  | 'video_avatar_circle'
  | 'image_avatar'
  | 'list_item'
  | 'avatar_image_split';

/**
 * Active Scene Types (supported in UI)
 * Deprecated types (video_avatar_circle, image_avatar, list_item) are kept in
 * SceneType for backward compatibility with existing DB records.
 */
export type ActiveSceneType =
  | 'avatar_only'
  | 'video_only'
  | 'avatar_broll_split'
  | 'avatar_image_split';

/**
 * Split Top Content Type
 * Defines what appears in the top half of avatar_broll_split scenes
 */
export type SplitTopContent = 'broll' | 'text_card';

/**
 * Scene Split Status Union
 * Tracks the progress of scene splitting for a reel
 */
export type SceneSplitStatus = 'not_split' | 'splitting' | 'split' | 'failed';

/**
 * Image Generation Status Union
 * Tracks the progress of image generation for a scene
 */
export type ImageGenerationStatus = 'pending' | 'generating' | 'completed' | 'failed';

/**
 * Video Generation Status Union
 * Tracks the progress of video generation for a scene
 */
export type VideoGenerationStatus = 'idle' | 'queued' | 'generating' | 'completed' | 'failed';

/**
 * Scene Editor Mode Union
 * Defines the current mode of a scene card
 */
export type SceneEditorMode = 'view' | 'edit';

// ============================================================================
// Document Interfaces
// ============================================================================

/**
 * Reel Scene Interface
 * Full scene document as stored in the database
 */
export interface IReelScene {
  _id: Id<'reelScenes'>;
  _creationTime: number;

  // Ownership & Relationships
  reelId: Id<'reels'>;
  userId: Id<'users'>;

  // Scene ordering
  sceneNumber: number;

  // AI-generated content
  voiceoverText: string;
  sceneDescription: string;
  imagePrompt: string;
  sceneType: SceneType;
  estimatedDurationSeconds: number;

  // User edits (optional overrides)
  isUserEdited: boolean;
  editedVoiceoverText?: string;
  editedSceneDescription?: string;
  editedImagePrompt?: string;
  editedSceneType?: SceneType;

  // Image generation tracking — first frame
  generatedImageId?: Id<'_storage'>;
  imageGenerationStatus?: ImageGenerationStatus;

  // Image generation tracking — last frame
  lastFrameImageId?: Id<'_storage'>;
  lastFrameImageGenerationStatus?: ImageGenerationStatus;

  // AI tracking
  aiSuggestedType: string;
  userOverriddenType: boolean;
  generatedByModel?: string;
  regenerationCount: number;

  // Reference Image Support (AI Avatar Feature)
  useReferenceImage?: boolean;
  referencePersonId?: Id<'referencePersons'>; // Legacy - kept for backward compatibility
  libraryImageId?: Id<'generatedAvatars'>; // Selected generated avatar from library

  // Video Generation (Image-to-Video Feature)
  videoPrompt?: string; // Editable video motion prompt
  videoPromptGeneratedAt?: number; // When AI generated the prompt
  videoGenerationStatus?: VideoGenerationStatus; // Overall video generation progress

  // Split Screen Support (avatar_broll_split)
  splitTopContent?: SplitTopContent;
  textCardTitle?: string;
  editedTextCardTitle?: string;
  splitAvatarVideoId?: Id<'_storage'>;
  splitAvatarVideoStatus?: VideoGenerationStatus;

  // Timestamps
  createdAt: number;
  updatedAt: number;
}

/**
 * Scene with effective values
 * Returns user-edited values if available, otherwise AI-generated
 */
export interface IReelSceneEffective {
  _id: Id<'reelScenes'>;
  sceneNumber: number;
  voiceoverText: string;
  sceneDescription: string;
  imagePrompt: string;
  sceneType: SceneType;
  estimatedDurationSeconds: number;
  isUserEdited: boolean;
  userOverriddenType: boolean;
  regenerationCount: number;
  generatedImageId?: Id<'_storage'>;
  imageGenerationStatus?: ImageGenerationStatus;
  textCardTitle?: string;
  splitTopContent?: SplitTopContent;
}

// ============================================================================
// State Interfaces
// ============================================================================

/**
 * Scene Splitting Phase
 */
export type SceneSplittingPhase = 'idle' | 'splitting' | 'split' | 'error';

/**
 * Scene Splitting State Interface
 * State for the scene splitting hook
 */
export interface ISceneSplittingState {
  phase: SceneSplittingPhase;
  scenes: IReelScene[];
  totalScenes: number;
  isLoading: boolean;
  isSplitting: boolean;
  error: string | null;
  selectedModelKey: ModelKey;
}

/**
 * Scene Editor State Interface
 * State for individual scene editing
 */
export interface ISceneEditorState {
  editingSceneId: Id<'reelScenes'> | null;
  mode: SceneEditorMode;
  pendingChanges: Partial<ISceneEditPayload> | null;
  isSaving: boolean;
  isRegenerating: boolean;
  regeneratingSceneId: Id<'reelScenes'> | null;
  error: string | null;
}

/**
 * Scene Edit Payload Interface
 * Data structure for scene edits
 */
export interface ISceneEditPayload {
  editedVoiceoverText?: string;
  editedSceneDescription?: string;
  editedImagePrompt?: string;
  editedSceneType?: SceneType;
  // Reference Image Support (AI Avatar Feature)
  useReferenceImage?: boolean;
  referencePersonId?: Id<'referencePersons'> | null; // Legacy
  libraryImageId?: Id<'generatedAvatars'> | null; // Selected generated avatar from library
  // Video Generation (Image-to-Video Feature)
  videoPrompt?: string; // User-edited video motion prompt
  // Split Screen Support (avatar_broll_split)
  editedTextCardTitle?: string;
}

// ============================================================================
// Action Types
// ============================================================================

/**
 * Scene Splitting Action Types
 */
export type SceneSplittingAction =
  | { type: 'START_SPLIT' }
  | { type: 'SPLIT_SUCCESS'; payload: { scenes: IReelScene[]; totalScenes: number } }
  | { type: 'SPLIT_ERROR'; payload: { error: string } }
  | { type: 'SET_MODEL'; payload: { modelKey: ModelKey } }
  | { type: 'SCENES_LOADED'; payload: { scenes: IReelScene[] } }
  | { type: 'SCENE_UPDATED'; payload: { scene: IReelScene } }
  | { type: 'SCENE_DELETED'; payload: { sceneId: Id<'reelScenes'> } }
  | { type: 'SCENES_REORDERED'; payload: { sceneOrder: Id<'reelScenes'>[] } }
  | { type: 'RESET' };

/**
 * Scene Editor Action Types
 */
export type SceneEditorAction =
  | { type: 'START_EDIT'; payload: { sceneId: Id<'reelScenes'> } }
  | { type: 'CANCEL_EDIT' }
  | { type: 'UPDATE_PENDING'; payload: Partial<ISceneEditPayload> }
  | { type: 'START_SAVE' }
  | { type: 'SAVE_SUCCESS' }
  | { type: 'SAVE_ERROR'; payload: { error: string } }
  | { type: 'START_REGENERATE'; payload: { sceneId: Id<'reelScenes'> } }
  | { type: 'REGENERATE_SUCCESS' }
  | { type: 'REGENERATE_ERROR'; payload: { error: string } }
  | { type: 'RESET' };

// ============================================================================
// Component Props Interfaces
// ============================================================================

/**
 * Scene Editor Container Props
 */
export interface ISceneEditorContainerProps {
  reelId: Id<'reels'>;
  sceneSplitStatus: SceneSplitStatus | undefined;
  hasSplitScenes?: boolean | undefined;
  reelTitle: string;
  onBack: () => void;
}

// ============================================================================
// Hook Return Types
// ============================================================================

/**
 * Use Scene Splitting Hook Return Type
 */
export interface IUseSceneSplittingReturn {
  // State
  state: ISceneSplittingState;

  // Actions
  splitReel: (modelKey?: ModelKey) => Promise<void>;
  setModel: (modelKey: ModelKey) => void;
  reset: () => void;

  // Computed
  canSplit: boolean;
  hasScenes: boolean;
}

/**
 * Use Scene Editor Hook Return Type
 */
export interface IUseSceneEditorReturn {
  // State
  state: ISceneEditorState;

  // Actions
  startEdit: (sceneId: Id<'reelScenes'>) => void;
  cancelEdit: () => void;
  saveScene: (sceneId: Id<'reelScenes'>, changes: ISceneEditPayload) => Promise<void>;
  deleteScene: (sceneId: Id<'reelScenes'>) => Promise<void>;
  regenerateScene: (
    sceneId: Id<'reelScenes'>,
    sceneType?: ActiveSceneType,
    modelKey?: ModelKey
  ) => Promise<void>;
  updatePending: (changes: Partial<ISceneEditPayload>) => void;
  reorderScenes: (sceneOrder: Id<'reelScenes'>[]) => Promise<void>;
  reset: () => void;

  // Computed
  isEditing: boolean;
  canSave: boolean;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Get effective scene values
 * Returns user-edited values if available, otherwise AI-generated
 */
export function getEffectiveSceneValues(scene: IReelScene): IReelSceneEffective {
  return {
    _id: scene._id,
    sceneNumber: scene.sceneNumber,
    voiceoverText: scene.editedVoiceoverText ?? scene.voiceoverText,
    sceneDescription: scene.editedSceneDescription ?? scene.sceneDescription,
    imagePrompt: scene.editedImagePrompt ?? scene.imagePrompt,
    sceneType: (scene.editedSceneType ?? scene.sceneType) as SceneType,
    estimatedDurationSeconds: scene.estimatedDurationSeconds,
    isUserEdited: scene.isUserEdited,
    userOverriddenType: scene.userOverriddenType,
    regenerationCount: scene.regenerationCount,
    generatedImageId: scene.generatedImageId,
    imageGenerationStatus: scene.imageGenerationStatus,
    textCardTitle: scene.editedTextCardTitle ?? scene.textCardTitle,
    splitTopContent: scene.splitTopContent,
  };
}

/**
 * Calculate total reel duration from scenes
 */
export function calculateTotalDuration(scenes: IReelScene[]): number {
  return scenes.reduce((sum, scene) => sum + scene.estimatedDurationSeconds, 0);
}
